import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography, Flex, Select, Tag, message, Spin, Input } from "antd";
import { SearchOutlined, UserOutlined, FilterOutlined, DownOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getCookie } from "../../../utils/cookieUtils";
import { PIPELINE_STAGES } from "./pipelineData";
import PipelineCard from "./PipelineCard";
import MoveStageModal from "./MoveStageModal";
import DealDetailsDrawer from "./DealDetailsDrawer";
import CompanyFullViewModal from "../leads-template/CompanyFullViewModal";
import QuotationModal from "../quotation/QuotationModal";
import ProposalModal from "../quick-access/ProposalModal";
import InvoiceModal from "../invoice/InvoiceModal";
import { getPipelineBoard, updateLeadPipelineStage } from "../../../services/pipelineService";
import { getEmployees, getEmployeeProfilePhotos } from "../../../services/employeeService";
import { getCompanyFullDetails } from "../../../services/leadService";
import "./pipeline.css";
import "../quick-overview/quickOverview.css";

const { Text } = Typography;

const getColumnClassName = (code) => {
  if (code === "CLOSED_WON") return "pipeline-col won-column";
  if (code === "CLOSED_LOST") return "pipeline-col lost-column";
  return "pipeline-col";
};

function PipelineWorkspace({
  role = "admin",
  employeeId: propEmployeeId,
  initialSearch = "",
  targetDealId = null,
  pipelineFilters,
  onPipelineFiltersChange,
  addDealTrigger,
  hideHeaderControls = false,
}) {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const effectiveRole = role || user?.role || "company_admin";
  const isEmployee = effectiveRole === "employee" || effectiveRole === "sales_rep";
  const currentEmployeeId = propEmployeeId || (isEmployee ? user?.id || user?._id || user?.employeeId : null);
  const currentEmployeeName = user?.name || user?.fullName || "";

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeePhotosMap, setEmployeePhotosMap] = useState({});

  // Filters (Internal or External via pipelineFilters)
  const [selectedOwner, setSelectedOwner] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  const activeSearch = pipelineFilters?.search !== undefined ? pipelineFilters.search : searchQuery;
  const activeOwner = pipelineFilters?.owner !== undefined ? pipelineFilters.owner : selectedOwner;
  const activePriority = pipelineFilters?.priority !== undefined ? pipelineFilters.priority : selectedPriority;

  // Move Modal State
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [movingDeal, setMovingDeal] = useState(null);
  const [targetStage, setTargetStage] = useState(null);

  // Deal Details Drawer
  const [drawerDeal, setDrawerDeal] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Company Full View Modal
  const [fullViewVisible, setFullViewVisible] = useState(false);
  const [selectedCompanyForFullView, setSelectedCompanyForFullView] = useState(null);
  const [selectedLeadForFullView, setSelectedLeadForFullView] = useState(null);

  // Quick Access Document Modals
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedLeadForDoc, setSelectedLeadForDoc] = useState(null);

  // Sync search when navigated here from a global search result
  useEffect(() => {
    if (initialSearch !== undefined) {
      setSearchQuery(initialSearch);
    }
  }, [initialSearch]);

  // Scroll to & highlight the target deal card after board loads
  useEffect(() => {
    if (!targetDealId || loading || deals.length === 0) return;
    // Small delay to let React finish painting the cards
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-deal-id="${targetDealId}"]`);
      if (!el) return;
      // Scroll the horizontal board to the column containing this card
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      // Apply a brief highlight pulse
      el.classList.add('pipeline-card-highlight');
      setTimeout(() => el.classList.remove('pipeline-card-highlight'), 2500);
    }, 300);
    return () => clearTimeout(timer);
  }, [targetDealId, loading, deals]);

  // Fetch Pipeline Board from Live Middleware
  const loadBoardData = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const filters = {};
      if (isEmployee && currentEmployeeId) {
        filters.employeeId = currentEmployeeId;
      } else if (activeOwner !== "all") {
        filters.owner = activeOwner;
      }
      if (activePriority !== "all") filters.priority = activePriority;
      if (activeSearch.trim()) filters.search = activeSearch.trim();

      const res = await getPipelineBoard(effectiveCompanyCode, filters, 100);
      if (res?.success && res.columns) {
        // Flatten column deals into single array
        const allDeals = [];
        Object.entries(res.columns).forEach(([stg, col]) => {
          if (col && Array.isArray(col.leads)) {
            col.leads.forEach((ld) => {
              allDeals.push({
                _id: ld._id || ld.id,
                leadId: ld.leadId || ld._id,
                leadCompanyName: ld.leadCompanyName || ld.companyName || "Lead Deal",
                contactName: ld.contactName || "",
                contactNumber: ld.contactNumber || ld.phone || "",
                assignedEmployeeName: ld.assignedEmployeeName && ld.assignedEmployeeName !== "Sales Rep" ? ld.assignedEmployeeName : "",
                assignedEmployeeId: ld.assignedEmployeeId || null,
                assignedEmployeePhone: ld.assignedEmployeePhone || null,
                createdByName: ld.createdByName || "",
                dealName: ld.dealName || `${ld.leadCompanyName || "Deal"} - License`,
                amount: Number(ld.amount || ld.quoteAmount || ld.totalAmount || 0),
                closingDate: ld.closingDate || ld.expectedCloseDate || null,
                priority: ld.priority || "Medium",
                pipelineStage: ld.pipelineStage || stg,
                stageChangedAt: ld.stageChangedAt || ld.updatedAt || ld.createdAt,
                description: ld.description || ld.companyDescription || "",
                customerNeed: ld.customerNeed || "",
                proposedSolution: ld.proposedSolution || "",
                ...ld,
              });
            });
          }
        });
        setDeals(allDeals);
      } else {
        setDeals([]);
      }
    } catch (err) {
      console.error("Failed to load pipeline board:", err);
      message.error("Failed to load pipeline.");
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode, isEmployee, currentEmployeeId, activeOwner, activePriority, activeSearch]);

  // Load Employees for owner filter and deal name resolution
  const loadEmployeesList = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    try {
      const res = await getEmployees(effectiveCompanyCode);
      if (res?.success && Array.isArray(res.employees)) {
        setEmployees(res.employees);
      }
    } catch (e) {
      console.warn("Failed to load employees list for pipeline filter", e);
    }
  }, [effectiveCompanyCode]);

  useEffect(() => {
    loadEmployeesList();
  }, [loadEmployeesList]);

  // Separate non-blocking background fetch for employee profile photos
  useEffect(() => {
    if (!effectiveCompanyCode) return;
    let isMounted = true;
    getEmployeeProfilePhotos(effectiveCompanyCode)
      .then((res) => {
        if (isMounted && res?.success && res.photos) {
          setEmployeePhotosMap(res.photos);
        }
      })
      .catch((err) => {
        console.warn("Non-blocking photo fetch failed:", err);
      });
    return () => {
      isMounted = false;
    };
  }, [effectiveCompanyCode]);

  useEffect(() => {
    loadBoardData();
  }, [loadBoardData]);

  // Map employee name dynamically
  const employeesMap = useMemo(() => {
    const map = new Map();
    employees.forEach((e) => {
      if (e._id) map.set(String(e._id), e.name);
      if (e.id) map.set(String(e.id), e.name);
      if (e.phone) map.set(String(e.phone), e.name);
      if (e.mobile) map.set(String(e.mobile), e.name);
    });
    return map;
  }, [employees]);

  // Filtered Deals: strictly scoped to logged in employee if role is employee
  const filteredDeals = useMemo(() => {
    return deals
      .map((d) => {
        const mappedEmpName =
          (d.assignedEmployeeId && employeesMap.get(String(d.assignedEmployeeId))) ||
          (d.assignedEmployeePhone && employeesMap.get(String(d.assignedEmployeePhone))) ||
          (d.assignedEmployeeName && d.assignedEmployeeName !== "Sales Rep" ? d.assignedEmployeeName : "") ||
          (d.employeeName && d.employeeName !== "Sales Rep" ? d.employeeName : "") ||
          d.createdByName ||
          (isEmployee ? currentEmployeeName : "");

        const mappedPhoto =
          (d.assignedEmployeeId && employeePhotosMap[String(d.assignedEmployeeId)]) ||
          (d.employeeId && employeePhotosMap[String(d.employeeId)]) ||
          (d.assignedEmployeePhone && employeePhotosMap[String(d.assignedEmployeePhone).trim()]) ||
          (mappedEmpName && employeePhotosMap[mappedEmpName.toLowerCase().trim()]) ||
          "";

        return {
          ...d,
          assignedEmployeeName: mappedEmpName,
          assignedEmployeePhoto: mappedPhoto,
        };
      })
      .filter((d) => {
        if (isEmployee && (currentEmployeeId || currentEmployeeName)) {
          const matchesId = currentEmployeeId && (String(d.assignedEmployeeId) === String(currentEmployeeId) || String(d.employeeId) === String(currentEmployeeId) || String(d.createdById) === String(currentEmployeeId));
          const matchesName = currentEmployeeName && (d.assignedEmployeeName?.toLowerCase() === currentEmployeeName.toLowerCase() || d.employeeName?.toLowerCase() === currentEmployeeName.toLowerCase() || d.createdByName?.toLowerCase() === currentEmployeeName.toLowerCase());
          if (!matchesId && !matchesName) return false;
        } else if (activeOwner !== "all" && String(d.assignedEmployeeId) !== String(activeOwner) && d.assignedEmployeeName !== activeOwner) {
          return false;
        }
        return activePriority === "all" || d.priority === activePriority;
      });
  }, [deals, employeesMap, employeePhotosMap, isEmployee, currentEmployeeId, currentEmployeeName, activeOwner, activePriority]);

  // Stage Columns with totals
  const columnsData = useMemo(() => {
    return PIPELINE_STAGES.map((stage) => {
      const stageDeals = filteredDeals.filter((d) => d.pipelineStage === stage.code || d.pipelineStage?.toUpperCase() === stage.code);
      const totalAmount = stageDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
      return {
        ...stage,
        deals: stageDeals,
        totalAmount,
        count: stageDeals.length,
      };
    });
  }, [filteredDeals]);

  // Drag & Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, stageCode) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData("dealId");
    if (!dealId) return;

    const deal = deals.find((d) => d._id === dealId || d.leadId === dealId);
    if (deal && deal.pipelineStage !== stageCode) {
      setMovingDeal(deal);
      setTargetStage(stageCode);
      setMoveModalVisible(true);
    }
  };

  const handleConfirmMove = async ({ dealId, targetStage, qualificationOutcome, lostReason, backwardReason, transitionData, isBackward }) => {
    try {
      const payload = {
        targetStage,
        stage: targetStage,
        qualificationOutcome,
        lostReason,
        backwardReason,
        isBackward,
        transitionData,
        ...transitionData,
      };
      const res = await updateLeadPipelineStage(dealId, payload, effectiveCompanyCode);
      if (res.success) {
        message.success(`Deal stage updated to ${PIPELINE_STAGES.find((s) => s.code === targetStage)?.label || targetStage}!`);
        loadBoardData();
      } else {
        message.error(res.message || "Failed to update stage.");
      }
    } catch (err) {
      console.error("Failed to update pipeline stage:", err);
      message.error("Failed to update pipeline stage.");
    }
  };

  const handleOpenQuotation = (deal, amount) => {
    setMoveModalVisible(false);
    setSelectedLeadForDoc({
      ...deal,
      dealName: deal.dealName,
      leadCompanyName: deal.leadCompanyName,
      companyName: deal.leadCompanyName,
      contactName: deal.contactName,
      contactNumber: deal.contactNumber,
      amount: amount || deal.amount,
      _id: deal.leadId || deal._id,
      dealId: deal._id,
      stage: deal.stage || deal.pipelineStage,
      targetStageOnSave: "PROPOSAL_QUOTE",
    });
    setQuotationModalOpen(true);
  };

  const handleOpenProposal = (deal, amount) => {
    setMoveModalVisible(false);
    setSelectedLeadForDoc({
      ...deal,
      dealName: deal.dealName,
      leadCompanyName: deal.leadCompanyName,
      companyName: deal.leadCompanyName,
      contactName: deal.contactName,
      contactNumber: deal.contactNumber,
      amount: amount || deal.amount,
      _id: deal.leadId || deal._id,
      dealId: deal._id,
      targetStageOnSave: "PROPOSAL_QUOTE",
    });
    setProposalModalOpen(true);
  };

  const handleOpenInvoice = (deal, amount) => {
    setMoveModalVisible(false);
    setSelectedLeadForDoc({
      ...deal,
      dealName: deal.dealName,
      leadCompanyName: deal.leadCompanyName,
      companyName: deal.leadCompanyName,
      contactName: deal.contactName,
      contactNumber: deal.contactNumber,
      amount: amount || deal.amount,
      _id: deal.leadId || deal._id,
      dealId: deal._id,
      stage: deal.stage || deal.pipelineStage,
      targetStageOnSave: "NEGOTIATION_REVIEW",
    });
    setInvoiceModalOpen(true);
  };

  const handleDocSaved = async (docType, savedDoc, bundledDealIds = []) => {
    const allDealIds = Array.from(
      new Set([
        ...(selectedLeadForDoc?.dealId ? [selectedLeadForDoc.dealId] : []),
        ...(Array.isArray(bundledDealIds) ? bundledDealIds : []),
      ])
    );

    if (selectedLeadForDoc?.targetStageOnSave && allDealIds.length > 0) {
      try {
        const finalAmount = Number(savedDoc?.total || savedDoc?.subtotal || savedDoc?.grandTotal || savedDoc?.amount || selectedLeadForDoc.amount || 0);
        await Promise.all(
          allDealIds.map((dId) =>
            updateLeadPipelineStage(
              dId,
              {
                targetStage: selectedLeadForDoc.targetStageOnSave,
                stage: selectedLeadForDoc.targetStageOnSave,
                amount: finalAmount,
                transitionData: {
                  ...(selectedLeadForDoc.targetStageOnSave === "PROPOSAL_QUOTE" ? { quoteAmount: finalAmount, proposalSentDate: new Date() } : { dealCloseAmount: finalAmount, expectedCloseDate: new Date() }),
                },
              },
              effectiveCompanyCode,
            )
          )
        );
        message.success(
          `${allDealIds.length > 1 ? `${allDealIds.length} deals` : "Deal"} updated to ${
            selectedLeadForDoc.targetStageOnSave === "PROPOSAL_QUOTE" ? "Proposal / Price Quote" : "Negotiation / Review"
          }!`
        );
      } catch (err) {
        console.error("Auto stage update error:", err);
      }
    }
    loadBoardData();
  };

  const handleOpenCompanyFullView = useCallback(
    async (deal) => {
      if (!deal) return;
      const compName = deal.leadCompanyName || deal.companyName || "";

      // Initialize with deal data immediately so the modal opens with zero delay
      const initialCompany = {
        name: compName,
        companyName: compName,
        leads: [deal],
        contacts: [deal],
        gstNumber: deal.gstNumber || deal.gstin || "",
      };
      setSelectedCompanyForFullView(initialCompany);
      setSelectedLeadForFullView(deal);
      setFullViewVisible(true);

      // Fetch extended company details to populate all directors/contacts/branches
      if (effectiveCompanyCode && compName) {
        try {
          const res = await getCompanyFullDetails(effectiveCompanyCode, compName, currentEmployeeId);
          if (res?.success && res.company) {
            setSelectedCompanyForFullView(res.company);
            const contactsList = res.company.contacts || res.company.leads || [];
            if (contactsList.length > 0) {
              const matched = contactsList.find(
                (c) => String(c._id) === String(deal.leadId || deal._id) || c.contactName === deal.contactName
              );
              setSelectedLeadForFullView(matched || contactsList[0] || deal);
            }
          }
        } catch (err) {
          console.warn("Could not fetch extended company details for full view:", err);
        }
      }
    },
    [effectiveCompanyCode, currentEmployeeId],
  );



  return (
    <Spin spinning={loading} tip="Loading pipeline...">
      <div className="pipeline-container">
        {/* ── TOP HEADER / FILTER BAR ── */}
        {!hideHeaderControls && (
          <Flex justify="space-between" align="center" wrap="wrap" gap={16} className="pipeline-header">
            <Flex align="center" gap={10} wrap="wrap">
              <Input
                className="qo-search-input"
                prefix={<SearchOutlined className="qo-control-icon" />}
                placeholder="Search company or deal..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
              />

              {!isEmployee && (
                <Select
                  className={`qo-filter-select ${selectedOwner !== "all" ? "active-filter" : ""}`}
                  value={selectedOwner}
                  onChange={setSelectedOwner}
                  suffixIcon={<DownOutlined />}
                  style={{ minWidth: 140 }}
                  options={[
                    {
                      value: "all",
                      label: (
                        <span className="qo-select-label">
                          <UserOutlined className="qo-filter-icon" /> All Reps
                        </span>
                      ),
                    },
                    ...employees.map((emp) => ({
                      value: emp._id,
                      label: (
                        <span className="qo-select-label">
                          <UserOutlined className="qo-filter-icon" /> {emp.name}
                        </span>
                      ),
                    })),
                  ]}
                />
              )}

              <Select
                className={`qo-filter-select ${selectedPriority !== "all" ? "active-filter" : ""}`}
                value={selectedPriority}
                onChange={setSelectedPriority}
                suffixIcon={<DownOutlined />}
                style={{ minWidth: 140 }}
                options={[
                  {
                    value: "all",
                    label: (
                      <span className="qo-select-label">
                        <FilterOutlined className="qo-filter-icon" /> All Priorities
                      </span>
                    ),
                  },
                  {
                    value: "High",
                    label: (
                      <span className="qo-select-label">
                        <span className="qo-prio-dot prio-high" /> High Priority
                      </span>
                    ),
                  },
                  {
                    value: "Medium",
                    label: (
                      <span className="qo-select-label">
                        <span className="qo-prio-dot prio-medium" /> Medium Priority
                      </span>
                    ),
                  },
                  {
                    value: "Low",
                    label: (
                      <span className="qo-select-label">
                        <span className="qo-prio-dot prio-low" /> Low Priority
                      </span>
                    ),
                  },
                ]}
              />
            </Flex>


          </Flex>
        )}

        {/* ── KANBAN BOARD STAGES ── */}
        <div className="pipeline-board-scroll hide-scrollbar">
          <div className="pipeline-board-layout">
            {columnsData.map((col) => (
              <div key={col.code} className={getColumnClassName(col.code)} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, col.code)}>
                {/* Column Header */}
                  <div className="pipeline-col-header">
                    <Flex justify="space-between" align="center">
                      <Text strong style={{ fontSize: "0.88rem", color: "#0f172a" }}>
                        {col.label}
                      </Text>
                      <Tag
                        style={{
                          borderRadius: 10,
                          background: "#f1f5f9",
                          border: "none",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          margin: 0,
                        }}
                      >
                        {col.count}
                      </Tag>
                    </Flex>
                    <Text type="secondary" style={{ fontSize: "0.75rem", display: "block", marginTop: 2 }}>
                      ₹{col.totalAmount.toLocaleString("en-IN")}
                    </Text>
                  </div>

                  {/* Deals List */}
                  <div className="pipeline-col-body hide-scrollbar">
                    {col.deals.length > 0 ? (
                      col.deals.map((deal) => (
                        <div key={deal._id || deal.leadId} data-deal-id={String(deal._id || deal.leadId || "")}>
                          <PipelineCard
                            deal={deal}
                            isEmployee={isEmployee}
                            onSelectDeal={handleOpenCompanyFullView}
                          />
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "2rem 0", textAlign: "center", color: "#94a3b8", fontSize: "0.8rem" }}>Drop deals here</div>
                    )}
                  </div>
                </div>
            ))}
          </div>
        </div>

        {/* Move Stage Modal */}
        <MoveStageModal visible={moveModalVisible} onClose={() => setMoveModalVisible(false)} deal={movingDeal} targetStage={targetStage} onConfirmMove={handleConfirmMove} onGenerateQuotation={handleOpenQuotation} onGenerateProposal={handleOpenProposal} onGenerateInvoice={handleOpenInvoice} />



        {/* Deal Details Drawer */}
        <DealDetailsDrawer visible={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} deal={drawerDeal} onStageChange={loadBoardData} />

        {/* Company Full View 360-degree modal */}
        <CompanyFullViewModal
          visible={fullViewVisible}
          onClose={() => {
            setFullViewVisible(false);
            setSelectedCompanyForFullView(null);
            setSelectedLeadForFullView(null);
            loadBoardData();
          }}
          company={selectedCompanyForFullView}
          lead={selectedLeadForFullView}
          role={effectiveRole}
        />

        {/* Document Generation Modals */}
        <QuotationModal
          open={quotationModalOpen}
          visible={quotationModalOpen}
          onClose={() => {
            setQuotationModalOpen(false);
            setSelectedLeadForDoc(null);
          }}
          prefillLead={selectedLeadForDoc}
          companyCode={effectiveCompanyCode}
          employeeId={currentEmployeeId}
          onSaveQuotation={(savedDoc, bundledDealIds) => handleDocSaved("Quotation", savedDoc, bundledDealIds)}
        />

        <ProposalModal
          open={proposalModalOpen}
          visible={proposalModalOpen}
          onClose={() => {
            setProposalModalOpen(false);
            setSelectedLeadForDoc(null);
          }}
          prefillLead={selectedLeadForDoc}
          companyCode={effectiveCompanyCode}
          employeeId={currentEmployeeId}
          onSaveProposal={(savedDoc) => handleDocSaved("Proposal", savedDoc)}
        />

        <InvoiceModal
          open={invoiceModalOpen}
          visible={invoiceModalOpen}
          onClose={() => {
            setInvoiceModalOpen(false);
            setSelectedLeadForDoc(null);
          }}
          prefillLead={selectedLeadForDoc}
          companyCode={effectiveCompanyCode}
          employeeId={currentEmployeeId}
          onSaveInvoice={(savedDoc) => handleDocSaved("Invoice", savedDoc)}
        />
      </div>
    </Spin>
  );
}

export default PipelineWorkspace;
